import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Grid, Button, SvgIcon, Stack, useMediaQuery, TextField, InputAdornment,
  useTheme,
} from '@mui/material';
import { SearchLg, SearchSm } from '../../../assets';
import { Catalogue, CatalogueFilterOptions, client } from '../../../app/services/api/orderManagementClient';
import useLogError from '../../../hooks/useLogError';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';

import CataloguesTable from './components/CataloguesTable';
import { Section } from '../../../components/ui/Section';
import { UserContext } from '../../../components/shared/useUser';

const ManageCatalogues = () => {
  const { t } = useTranslation('catalogues');
  const theme = useTheme();
  const { selectedStore } = useContext(UserContext);
  const { logError } = useLogError();
  const [isLoading, setIsLoading] = useState(true);  
  const [catalogues, setCatalogues] = useState<Catalogue[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState('');
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  useEffect(() => {
    fetchCatalogues(getFilterOptions(false));
  }, [page, order, orderBy]);

  const fetchCatalogues = (filters: CatalogueFilterOptions) => {
    setIsLoading(true);
    client.getCatalogues(filters)
      .then((response) => {
        setCatalogues([...response.data.items]);
        setTotalPages(response.data.totalPages);
      })
      .catch(logError)
      .finally(() => setIsLoading(false));
  };
  const getFilterOptions = (resetPage: boolean) => {
    const filterOptions: CatalogueFilterOptions = {
      name: searchTerm,
      pageNumber: resetPage ? 0 : page,
      pageSize: 10,
      sort: orderBy,
      direction: order,
      storeNumber: selectedStore?.storeNumber,
    };
    return filterOptions;
  };
  const clearAll = () => {
    fetchCatalogues({
      name: '',
      pageNumber: 0,
      pageSize: 10,
      sort: orderBy,
      direction: order,
      storeNumber: selectedStore?.storeNumber,
    });
    setSearchTerm('');
    setPage(0);
  };

  const handleSearch = () => {
    fetchCatalogues(getFilterOptions(true));
    setPage(0);
  };

  return (
    <PageArea>
      <Box
        display='flex'
        width='100%'
        height='100%'
        flexDirection='column'
      >
        <Grid container>
          <Stack
            width='100%'
            spacing={2}
            direction={isMobile ? 'column' : 'row'}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Box width={isMobile ? '100%' : 'auto'}>
              <PageTitle subTitle={t('subtitle')}>{t('title')}</PageTitle>
            </Box>
          </Stack>
          <Grid container
            sx={{ pt: '6px', pb: '12px' }}
            spacing={4}>            <Grid item
              lg={3}
              sm={4}
              xs={12}>
              <TextField
                fullWidth
                size='small'
                placeholder={t('filterPanel.search')}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchLg />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item
              lg={2}
              sm={2}
              xs={12}>
              <Button
                variant="secondary"
                size="lg"
                onClick={clearAll}
                fullWidth
              >
                {t('filterPanel.clear')}
              </Button>
            </Grid>
            <Grid item
              lg={2}
              sm={2}
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'right' }}>
              <Button
                variant="primary"
                size="lg"
                startIcon={<SvgIcon> <SearchSm /> </SvgIcon>}
                onClick={handleSearch}
                fullWidth>
                {t('filterPanel.search')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Section>
          <CataloguesTable
            list={catalogues}
            isLoading={isLoading}
            searchTerm={searchTerm}
            clearAll={clearAll}
            order={order}
            setOrder={setOrder}
            orderBy={orderBy}
            setOrderBy={setOrderBy}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
          />
        </Section>
      </Box>
    </PageArea>
  );
};

export default ManageCatalogues;